import { FC, ReactNode, useRef } from 'react';

import { TokenBaseStaticSpace } from '@hh.ru/magritte-design-tokens/types';
import {
    BottomSheet,
    BottomSheetFooter,
    Button,
    NavigationBar,
    Text,
    Tooltip,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { QuestionCircleOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import styles from 'components/common/MagritteFormField/MagritteFormField.module.less';

interface MagritteFormFieldProps {
    input: ReactNode;
    label?: ReactNode;
    labelAdditionalContent?: ReactNode;
    labelTooltipContent?: ReactNode;
    vSpacingBottom?: TokenBaseStaticSpace;
    hint?: ReactNode;
}

const TrlKeys = {
    ok: 'common.MagritteFormField.ok',
};

export const MagritteFormField: FC<MagritteFormFieldProps> = ({
    input,
    label,
    labelAdditionalContent,
    labelTooltipContent,
    hint,
    vSpacingBottom,
}) => {
    const [showBottomSheet, setShowBottomSheetOn, setShowBottomSheetOff] = useOnOffState(false);
    const [showTooltip, setShowTooltipOn, setShowTooltipOff] = useOnOffState(false);

    const activatorRef = useRef<HTMLSpanElement | null>(null);
    const trls = useTrls();
    const { isMobile } = useBreakpoint();

    return (
        <div>
            {label && (
                <>
                    <div className={labelAdditionalContent ? styles.labelWithAdditionalContentWrapper : ''}>
                        <label
                            tabIndex={0}
                            className={styles.labelWrapper}
                            onClick={setShowBottomSheetOn}
                            onMouseEnter={isMobile ? undefined : setShowTooltipOn}
                            onMouseLeave={isMobile ? undefined : setShowTooltipOff}
                            onFocus={setShowTooltipOn}
                            onBlur={setShowTooltipOff}
                        >
                            <div className={styles.labelText}>
                                <Text style="secondary" typography="label-2-regular">
                                    {label}
                                </Text>
                            </div>
                            {labelTooltipContent && (
                                <QuestionCircleOutlinedSize16 initialColor="secondary" ref={activatorRef} />
                            )}
                        </label>
                        {labelAdditionalContent}
                    </div>
                    {labelTooltipContent && (
                        <>
                            <Tooltip
                                visible={showTooltip}
                                activatorRef={activatorRef}
                                placement="top-center"
                                data-qa="form-label-tooltip"
                                size="medium"
                            >
                                {labelTooltipContent}
                            </Tooltip>
                            <BottomSheet
                                visible={showBottomSheet}
                                onClose={setShowBottomSheetOff}
                                header={
                                    <NavigationBar
                                        title={<Text typography="title-5-semibold">{label}</Text>}
                                        showDivider="always"
                                    />
                                }
                                footer={
                                    <BottomSheetFooter>
                                        <Button
                                            stretched
                                            mode="secondary"
                                            style="neutral"
                                            onClick={setShowBottomSheetOff}
                                        >
                                            {trls(TrlKeys.ok)}
                                        </Button>
                                    </BottomSheetFooter>
                                }
                            >
                                <Text typography="label-2-regular">{labelTooltipContent}</Text>
                                <VSpacing default={16} />
                            </BottomSheet>
                        </>
                    )}
                </>
            )}
            <VSpacing default={12} />
            {input}
            {hint && (
                <>
                    <VSpacing default={4} />
                    <Text typography="paragraph-1-regular" style="secondary">
                        {hint}
                    </Text>
                </>
            )}
            {vSpacingBottom && <VSpacing default={vSpacingBottom} />}
        </div>
    );
};
